import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { toast } from "../../../lib/ui";
import type { Role } from "../../../types/role";

// Structural copies of ChatLayout's types (ground rule 5: no back-imports).
type VoidWS = { id: string; name: string; avatarUrl?: string | null; ownerId?: string | number; memberRole?: string; homeChannelId?: string | null; joinRequestCount?: number };
type Channel = { allowedRoles?: string[] | null };

export type SpaceSearchResult = {
  id: string;
  name?: string;
  avatarUrl?: string | null;
  description?: string | null;
  requestStatus?: string | null;
};

// Module copies of ChatLayout's pure key helpers (useAndroidShare pattern).
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
const normalizeChannelId = (chan: string) => (chan.includes(':') ? chan.split(':').slice(-1)[0] : chan);

type UseSpacesOptions = {
  socket: any;
  token: string;
  me: { userId?: string | null };
  user?: { userId?: string | null } | null;
  currentVoidId: string;
  currentVoidIdRef: { current: string };
  setCurrentVoidId: (id: string) => void;
  setCurrentChannelId: (id: string) => void;
};

/**
 * Space-list state and lifecycle (step 4.3): `voids` + the `void:list`
 * socket handler, rail order (`spaceOrder` + localStorage),
 * vaulted spaces (`hiddenSpaces` + the `echo:hiddenSpaces` settings event),
 * per-space roles (`spaceRoles`, `myRoleIdsBySpace`) with the derived
 * `isSpaceOwner`/`canAccessChannel` access gates, the create/join-space
 * surface (modal flag, welcome-flow events, search/join/create API actions),
 * and the `currentSpace`/`nonDmVoids` memos + the `__echoVoids` Vault cache.
 *
 * The socket effect's deps are just `[socket]`: onVoidList reaches the
 * current conversation only through refs (`currentVoidIdRef`,
 * `initialSpaceSetRef`) and stable setters, so unlike the shared effect it
 * was lifted from it never needs to re-register (same argument as
 * onVoicePresence in 2.2b).
 *
 * DELIBERATE SCOPE DEVIATION from the plan doc: `switchVoid` did NOT move.
 * It is cross-cluster navigation (presence, unread, DMs, channel locks,
 * members, folders, route props), and it is mutually entangled with
 * useChannelLocks (the hook takes switchVoid; switchVoid uses the hook's
 * pendingChannelSwitchRef) — that only works today because function
 * declarations hoist. It stays in the composition layer until Phase 7;
 * it consumes this hook's `voids` and the join/create actions consume the
 * same navigation setters this hook takes for onVoidList's first-load pick.
 */
export function useSpaces({
  socket,
  token,
  me,
  user,
  currentVoidId,
  currentVoidIdRef,
  setCurrentVoidId,
  setCurrentChannelId,
}: UseSpacesOptions) {
  const [voids, setVoids] = useState<VoidWS[]>([]);
  const [spaceRoles, setSpaceRoles] = useState<Role[]>([]);
  const [myRoleIdsBySpace, setMyRoleIdsBySpace] = useState<Record<string, string[]>>({});
  // Load roles for access controls
  useEffect(() => {
    const tok = getAuthToken() || '';
    if (!tok || !currentVoidId || currentVoidId.startsWith('dm_')) { setSpaceRoles([]); return; }
    (async () => {
      try {
        const res = await api.getAuth(`/spaces/${encodeURIComponent(currentVoidId)}/roles`, tok);
        setSpaceRoles(res.roles || []);
      } catch { setSpaceRoles([]); }
    })();
  }, [currentVoidId, token]);
  // Load the current user's roles for the active space to gate channel visibility
  useEffect(() => {
    const loadRoles = async () => {
      if (!currentVoidId || String(currentVoidId).startsWith('dm_')) {
        setMyRoleIdsBySpace((prev) => ({ ...prev, [currentVoidId]: [] }));
        return;
      }
      const tok = getAuthToken() || '';
      const uid = me.userId || user?.userId;
      if (!tok || !uid) return;
      try {
        const res = await api.getAuth(`/spaces/${encodeURIComponent(currentVoidId)}/members/${encodeURIComponent(uid)}/roles`, tok);
        const ids = Array.isArray(res.roles) ? res.roles.map((r: any) => String(r.id)) : [];
        setMyRoleIdsBySpace((prev) => ({ ...prev, [currentVoidId]: ids }));
      } catch {
        /* best effort; ignore */
      }
    };
    loadRoles();
  }, [currentVoidId, me.userId, user?.userId]);
  // One-shot guard so we only auto-redirect away from DMs on first load
  const initialSpaceSetRef = useRef<boolean>(false);
  // Reorder state persisted locally
  const [spaceOrder, setSpaceOrder] = useState<string[]>(() => {
    try { const s = localStorage.getItem('spaceOrder'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem('spaceOrder', JSON.stringify(spaceOrder)); } catch { /* noop */ } }, [spaceOrder]);
  const currentSpace = useMemo(() => voids.find((v) => v.id === currentVoidId), [voids, currentVoidId]);
  // Expose a lightweight spaces cache for settings Vault section
  useEffect(() => {
    try {
      (window as typeof window & { __echoVoids?: typeof voids }).__echoVoids = voids;
      const cache = voids.filter(v=>!String(v.id).startsWith('dm_')).map(v=>({ id: v.id, name: v.name||v.id }));
      localStorage.setItem('__echo_spaces_cache__', JSON.stringify(cache));
    } catch { /* noop */ }
  }, [voids]);
  const isSpaceOwner = useMemo(() => {
    if (!currentVoidId || String(currentVoidId).startsWith('dm_')) return false;
    if (!me.userId) return false;
    if (currentSpace?.memberRole) return String(currentSpace.memberRole).toLowerCase() === 'owner';
    if (currentSpace?.ownerId != null) return String(currentSpace.ownerId) === String(me.userId);
    return false;
  }, [currentVoidId, currentSpace?.memberRole, currentSpace?.ownerId, me.userId]);
  const canAccessChannel = useCallback((channel?: Channel | null) => {
    if (!channel) return false;
    if (isSpaceOwner) return true;
    const acl = channel.allowedRoles;
    if (!acl || acl.length === 0) return true;
    const myRoles = myRoleIdsBySpace[currentVoidId] || [];
    return acl.some((rid) => myRoles.includes(String(rid)));
  }, [isSpaceOwner, myRoleIdsBySpace, currentVoidId]);
  const nonDmVoids = useMemo(() => voids.filter((v) => !String(v.id).startsWith('dm_')), [voids]);
  // Hidden spaces sent to the Vault (excluded from lists)
  const [hiddenSpaces, setHiddenSpaces] = useState<Record<string, boolean>>(() => {
    try { const s = localStorage.getItem('hiddenSpaces'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem('hiddenSpaces', JSON.stringify(hiddenSpaces)); } catch { /* noop */ } }, [hiddenSpaces]);
  useEffect(() => {
    const onHidden = (e: Event) => {
      try {
        const hs = (e as CustomEvent<Record<string, boolean> | undefined>).detail;
        if (hs && typeof hs === 'object') setHiddenSpaces(hs);
      } catch { /* noop */ }
    };
    window.addEventListener('echo:hiddenSpaces', onHidden as EventListener);
    return () => window.removeEventListener('echo:hiddenSpaces', onHidden as EventListener);
  }, []);
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);
  function createSpace() {
    setCreateSpaceOpen(true);
  }
  // Listen for welcome flow actions dispatched from App.tsx
  useEffect(() => {
    const onOpenCreate = () => setCreateSpaceOpen(true);
    const onOpenJoin = () => setCreateSpaceOpen(true); // CreateSpaceModal has a join tab
    window.addEventListener('echo:open-create-space', onOpenCreate);
    window.addEventListener('echo:open-join-space', onOpenJoin);
    return () => {
      window.removeEventListener('echo:open-create-space', onOpenCreate);
      window.removeEventListener('echo:open-join-space', onOpenJoin);
    };
  }, []);

  async function searchSpaces(query: string): Promise<SpaceSearchResult[]> {
    const q = String(query || '').trim();
    if (q.length < 2) return [];
    const res = await api.getAuth(`/spaces/search?query=${encodeURIComponent(q)}`, token);
    return Array.isArray(res?.spaces) ? res.spaces : [];
  }

  async function requestJoinSpace(spaceId: string): Promise<{ status: string }> {
    const sid = String(spaceId || '').trim();
    if (!sid) throw new Error('Space id required');
    return api.postAuth(`/spaces/${encodeURIComponent(sid)}/join-request`, {}, token);
  }

  async function joinSpaceWithCode(rawCode: string) {
    const code = String(rawCode || '').trim();
    if (!code) {
      toast('Invite code required', 'error');
      return;
    }
    try {
      const { spaceId } = await api.postAuth('/invites/accept', { code }, token);
      socket.emit('void:list');
      setCurrentVoidId(spaceId);
      setCurrentChannelId('general');
      socket.emit('void:switch', { voidId: spaceId });
      socket.emit('channel:list', { voidId: spaceId });
      socket.emit('channel:switch', { voidId: spaceId, channelId: `${spaceId}:general` });
      setCreateSpaceOpen(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to accept invite';
      toast(msg || 'Failed to accept invite', 'error');
    }
  }

  async function handleCreateSpace(payload: { name: string; channels: { key: string; name: string; type: string }[]; homeKey: string }) {
    try {
      const res = await api.postAuth('/spaces', { name: payload.name }, token);
      const sid = String(res.id);
      const created: Record<string, { id: string; name: string }> = {};
      for (const ch of payload.channels) {
        // POST /spaces already creates a default 'general' text channel server-side,
        // so reuse it instead of POSTing a duplicate — that 400'd with "channel id
        // taken" and left created.general undefined, which then made
        // normalizeChannelId(undefined) throw "Cannot read properties of undefined".
        if (ch.key === 'general') {
          created[ch.key] = { id: `${sid}:general`, name: ch.name };
          continue;
        }
        try {
          const cres = await api.postAuth('/channels', { spaceId: sid, name: ch.name, type: ch.type }, token);
          created[ch.key] = { id: String(cres.id), name: ch.name };
        } catch (e) {
          const msg = e instanceof Error ? e.message : `Failed to create channel ${ch.name}`;
          toast(msg || `Failed to create channel ${ch.name}`, 'error');
        }
      }
      const home = created[payload.homeKey] || created['general'];
      if (home) {
        try { await api.patchAuth('/spaces', { spaceId: sid, homeChannelId: home.id }, token); } catch { /* noop */ }
      }
      socket.emit('void:list');
      setCurrentVoidId(sid);
      const gotoChannelId = (home?.id) || `${sid}:general`;
      // Select by fully-qualified channel id (the form currentChannelId stores), so
      // the post-create channel:list reconciliation keeps us on it instead of
      // bouncing to the first channel — and so an undefined `home` can't throw.
      setCurrentChannelId(gotoChannelId);
      socket.emit('void:switch', { voidId: sid });
      socket.emit('channel:list', { voidId: sid });
      socket.emit('channel:switch', { voidId: sid, channelId: gotoChannelId });
      setCreateSpaceOpen(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to create space';
      toast(msg || 'Failed to create space', 'error');
    }
  }

  useEffect(() => {
    if (!socket) return;

    const onVoidList = ({ voids }: { voids: VoidWS[] }) => {
      setVoids(voids);
      // Seed order with any new spaces
      setSpaceOrder((prev) => {
        const known = new Set(prev);
        const next = prev.filter(id => voids.find(v=>v.id===id));
        for (const v of voids) if (!known.has(v.id)) next.push(v.id);
        return next;
      });
        // Only on first load: if current space is empty or a DM, switch to a non-DM space
        if (!initialSpaceSetRef.current) {
          initialSpaceSetRef.current = true;
          const current = currentVoidIdRef.current;
          if (!current || String(current).startsWith('dm_')) {
            const nonDm = voids.find(v => !String(v.id).startsWith('dm_'));
            if (nonDm) {
              const home = nonDm.homeChannelId as string | undefined;
              const short = normalizeChannelId(home || 'general');
              setCurrentVoidId(nonDm.id);
              socket.emit('void:switch', { voidId: nonDm.id });
              socket.emit('channel:list', { voidId: nonDm.id });
              setCurrentChannelId(short);
              socket.emit('channel:switch', { voidId: nonDm.id, channelId: fq(nonDm.id, short) });
            }
          }
        }
      };
    socket.on("void:list", onVoidList);
    return () => {
      socket.off("void:list", onVoidList);
    };
  }, [socket, currentVoidIdRef, setCurrentChannelId, setCurrentVoidId]);

  return {
    voids,
    setVoids,
    spaceOrder,
    setSpaceOrder,
    hiddenSpaces,
    setHiddenSpaces,
    spaceRoles,
    currentSpace,
    nonDmVoids,
    isSpaceOwner,
    canAccessChannel,
    createSpaceOpen,
    setCreateSpaceOpen,
    createSpace,
    searchSpaces,
    requestJoinSpace,
    joinSpaceWithCode,
    handleCreateSpace,
  };
}


